import { useEffect, useRef, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type {
  ResidentApplicationData,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '../types'
import {
  computeMaxMoveInDateAndReason,
  momentDiffMonths,
  parseYmd,
} from '../utils/residentCalculationClientFallback'

function toYmd(value: unknown): string {
  const s = String(value ?? '').trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)

  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s)
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`
  }

  const dmy = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s)
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  }

  return ''
}

/** Normalize month so "09" and "9" are treated as the same (avoid false first-month edits). */
function monthKey(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw || !/^\d+$/.test(raw)) return raw
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1 || n > 12) return raw
  return String(n).padStart(2, '0')
}

function getDetailRecordKey(detail: ResidentDetailResponse): string {
  const rd = detail.resident_default as Record<string, unknown> | undefined
  const unit = detail.unit_key as Record<string, unknown> | undefined
  const id =
    rd?.fk_reimbursement_id ??
    rd?.fk_resident_default_id ??
    rd?.reimbursement_id ??
    rd?.id ??
    rd?._id ??
    rd?.default_id ??
    unit?.id ??
    unit?._id
  if (id !== undefined && id !== null && String(id).trim() !== '') {
    return String(id)
  }
  return [
    toYmd(rd?.adjust_max_movein_date),
    String(rd?.rental_status_date ?? ''),
    String(rd?.reimbursement_end_date_override_source ?? ''),
    String(unit?.movein_date ?? ''),
  ].join('|')
}

function isManualEndDateOverride(rd: Record<string, unknown> | undefined): boolean {
  if (!rd) return false
  return (
    String(rd.reimbursement_end_date_override_source ?? '') === 'Manual Override' ||
    String(rd.reimbursement_end_date_override ?? '') === '1'
  )
}

type EndDatePatchForm = Pick<
  UseFormReturn<ResidentDefaultFormValues>,
  'watch' | 'getValues' | 'setValue' | 'formState'
>

/**
 * Angular `calculationBaseonDate()` patches `adjust_max_movein_date` from the system max
 * unless a Manual Override is preserved (cleared when first month not paid changes).
 * Also syncs read-only `lease_month` into the form for save payloads (with or without unit_key).
 *
 * React hydrate/refresh: keep persisted DB end date. Only write the system max after the user
 * edits calc inputs (dirty fields) — not when other effects hydrate/normalize dates/months.
 */
export function useLiveReimbursementEndDatePatch(args: {
  form: EndDatePatchForm
  detail: ResidentDetailResponse | null | undefined
  applicationData: ResidentApplicationData | null | undefined
}) {
  const { form, detail, applicationData } = args
  const preserveOverrideRef = useRef(false)
  const prevFirstMonthKeyRef = useRef<string | null>(null)
  const recordKeyRef = useRef<string | null>(null)
  const [isOverrideCleared, setIsOverrideCleared] = useState(false)

  const adjustedNotice = form.watch('adjusted_notice')
  const rentalStatusDate = form.watch('rental_status_date')
  const firstMonthM = form.watch('first_month_not_paid_month')
  const firstMonthY = form.watch('first_month_not_paid_year')
  const newResidentLease = form.watch('new_resident_lease_start_date')

  useEffect(() => {
    const values = form.getValues() as ResidentDefaultFormValues
    const contractStart = parseYmd(applicationData?.contract_start_date || applicationData?.lease_start_date)
    const rStatusDate = parseYmd(rentalStatusDate || values.rental_status_date)

    let computedLeaseMonth: string | number | undefined
    if (detail?.unit_key) {
      const contractStartForLease = parseYmd(
        applicationData?.contract_start_date || applicationData?.lease_start_date,
      )
      if (contractStartForLease && rStatusDate) {
        computedLeaseMonth = momentDiffMonths(contractStartForLease, rStatusDate)
      }
    } else if (contractStart && rStatusDate) {
      computedLeaseMonth = momentDiffMonths(contractStart, rStatusDate)
    }

    if (
      computedLeaseMonth !== undefined &&
      computedLeaseMonth !== null &&
      computedLeaseMonth !== ''
    ) {
      const nextLease = String(computedLeaseMonth)
      if (String(form.getValues('lease_month') ?? '') !== nextLease) {
        form.setValue('lease_month', nextLease, { shouldDirty: false })
      }
    }

    if (!detail?.unit_key) return

    const rd = detail.resident_default as Record<string, unknown> | undefined
    const isManualOverride = isManualEndDateOverride(rd)
    const recordKey = getDetailRecordKey(detail)
    const dirtyFields = form.formState.dirtyFields as Partial<
      Record<keyof ResidentDefaultFormValues, boolean>
    >

    if (recordKeyRef.current !== recordKey) {
      recordKeyRef.current = recordKey
      prevFirstMonthKeyRef.current = null
      preserveOverrideRef.current = isManualOverride
      setIsOverrideCleared(false)
    }

    const firstMonthKey = `${monthKey(firstMonthM)}|${String(firstMonthY ?? '').trim()}`
    const firstMonthValueChanged =
      prevFirstMonthKeyRef.current !== null && prevFirstMonthKeyRef.current !== firstMonthKey
    prevFirstMonthKeyRef.current = firstMonthKey

    // Angular clears override only when the user changes first month — not on hydrate
    // (empty → "09|2024") or month padding normalize ("09" ↔ "9").
    const firstMonthUserEdited =
      firstMonthValueChanged &&
      (Boolean(dirtyFields.first_month_not_paid_month) ||
        Boolean(dirtyFields.first_month_not_paid_year))

    if (firstMonthUserEdited) {
      preserveOverrideRef.current = false
      setIsOverrideCleared(true)
    }

    const shouldPreserve =
      preserveOverrideRef.current || (isManualOverride && !firstMonthUserEdited)

    const apiEndDate = toYmd(rd?.adjust_max_movein_date)
    const current = toYmd(form.getValues('adjust_max_movein_date'))

    // Always restore persisted DB end date when the picker is empty.
    if (!current && apiEndDate) {
      form.setValue('adjust_max_movein_date', apiEndDate, {
        shouldDirty: false,
        shouldValidate: true,
      })
    }

    if (shouldPreserve) return

    const userEditedCalcInputs =
      Boolean(dirtyFields.adjusted_notice) ||
      Boolean(dirtyFields.rental_status_date) ||
      Boolean(dirtyFields.first_month_not_paid_month) ||
      Boolean(dirtyFields.first_month_not_paid_year) ||
      Boolean(dirtyFields.new_resident_lease_start_date)

    // Refresh/hydrate/programmatic sync must not replace DB end date with system max.
    if (!userEditedCalcInputs) return

    const computedMaxMoveInDate = computeMaxMoveInDateAndReason(
      form.getValues() as ResidentDefaultFormValues,
      applicationData,
      detail,
    ).max_movein_date
    const next = toYmd(computedMaxMoveInDate)
    if (!next) return

    const latest = toYmd(form.getValues('adjust_max_movein_date'))
    if (latest === next) return

    form.setValue('adjust_max_movein_date', next, { shouldDirty: false, shouldValidate: true })
  }, [
    form,
    detail,
    applicationData,
    adjustedNotice,
    rentalStatusDate,
    firstMonthM,
    firstMonthY,
    newResidentLease,
  ])

  return {
    isOverrideCleared,
    markEndDateOverridePreserved: () => {
      preserveOverrideRef.current = true
      setIsOverrideCleared(false)
    },
  }
}
